import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, MapPin, ExternalLink, ArrowRight } from "lucide-react";
import axios from "axios";
import { PortfolioPreview } from "./Portfolio";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicPortfolio() {
  const { username } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/portfolio/public/${username}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Portfolio not found"));
  }, [username]);

  if (error) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center p-6 text-center">
        <div className="font-display font-black text-3xl mb-2">{error}</div>
        <div className="text-sm text-muted-foreground mb-6">This portfolio may be private or the username is incorrect.</div>
        <Link to="/">
          <Button variant="outline">Back to Career OS <ArrowRight size={14} className="ml-2" /></Button>
        </Link>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="animate-pulse font-mono text-sm text-muted-foreground">Loading portfolio…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="public-portfolio-page">
      <header className="glass-surface sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <Home size={14} /> Career OS
          </Link>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            {data.location && (
              <span className="inline-flex items-center gap-1 font-mono text-xs">
                <MapPin size={12} /> {data.location}
              </span>
            )}
            <Link to={`/u/${data.username}`} className="hover:text-foreground inline-flex items-center gap-1 transition-colors">
              Full profile <ExternalLink size={12} />
            </Link>
          </div>
        </div>
      </header>
      <motion.main
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="max-w-4xl mx-auto"
      >
        <PortfolioPreview name={data.name} content={data.content} />
      </motion.main>
      <footer className="border-t border-border py-8 text-center text-xs text-muted-foreground font-mono uppercase tracking-widest">
        Built on <Link to="/" className="text-primary hover:underline">Career OS</Link>
      </footer>
    </div>
  );
}
